import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { CreateEditStatisticModalComponent } from '@connect-services/client/components/modals/create-edit-statistic-modal/create-edit-statistic-modal.component';
import { ContentLayoutService, LAYOUT_KEY } from '@connect-services/client/services/content-layout.service';
import { SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    ComputedStatistic,
    ComputedStatisticsResponse,
    DepositDataVersion,
    PersonStatisticStandard,
    ProjectsService,
    StatisticsService,
    StatisticToValidateDto,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export type LayersAndEntitiesSelection = { entities: string[]; layers: string[] };

export const STATISTICS_FILTER_LOCAL_STORAGE_KEY = 'exportStatisticsGeographicFilter';

export const STATISTIC_CLASS_FULL = 'basis-full max-w-full';
export const STATISTIC_CLASS_HALF = 'basis-[calc(50%-0.5rem)] max-w-[calc(50%-0.5rem)]';

@Injectable({
    providedIn: 'root',
})
export class StatisticService {
    public readonly computedStatisticList = signal<ComputedStatistic[]>([]);
    public readonly dataVersions = signal<DepositDataVersion[]>([]);
    public readonly isLoading = signal<boolean>(false);
    public readonly isMapExpanded = computed(
        () => this.contentLayoutService.activeLayoutKey() === LAYOUT_KEY.TWO_THIRDS,
    );
    public readonly isStatisticActive = signal(false);
    public readonly pendingCharts = signal<string[]>([]);
    public readonly personStatisticOrder: WritableSignal<PersonStatisticStandard | null> = signal(null);
    public readonly projectId: WritableSignal<string> = signal('');
    public readonly selectedLayersAndEntities: WritableSignal<LayersAndEntitiesSelection | undefined> =
        signal(undefined);
    public readonly standardId = signal('');
    public readonly versionAndDataName = signal<SelectOption<string>[]>([]);
    public readonly wktStringMultipolygon: WritableSignal<string | undefined> = signal(undefined);

    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectsService = inject(ProjectsService);
    private readonly sdkStatisticService = inject(StatisticsService);
    private readonly snackBarService = inject(SnackBarService);

    public constructor() {
        effect(() => {
            if (!this.projectBloc.value()) {
                return;
            }
            this.projectId.set(this.projectBloc.value()?.id || '');
        });
    }

    public deleteStatistic(statisticId: string): Promise<void> {
        return firstValueFrom(
            this.sdkStatisticService.statisticControllerDelete({
                statisticId: statisticId,
            }),
        );
    }

    public getClassByRenderType(statistic: ComputedStatistic): string {
        if ([ComputedStatistic.RenderEnum.LINE, ComputedStatistic.RenderEnum.COLUMN].includes(statistic.render)) {
            return STATISTIC_CLASS_FULL;
        }

        if (this.isHalfStatistic(statistic)) {
            return STATISTIC_CLASS_HALF;
        }

        return STATISTIC_CLASS_FULL;
    }

    public async hideStatisticForUser(personId: string, statisticId: string): Promise<void> {
        const currentPersonStatisticStandard: PersonStatisticStandard | null = this.personStatisticOrder();

        if (!currentPersonStatisticStandard) {
            await this.setStatisticOrder(personId);
        }

        this.personStatisticOrder.update((personStatisticStandard: PersonStatisticStandard | null) => {
            if (!personStatisticStandard) {
                return personStatisticStandard;
            }

            const order = personStatisticStandard.statisticOrder ?? [];
            const hidden = personStatisticStandard.hiddenStatistics ?? [];

            return {
                ...personStatisticStandard,
                hiddenStatistics: hidden.includes(statisticId) ? hidden : [...hidden, statisticId],
                statisticOrder: order.filter((id: string) => id !== statisticId),
            };
        });

        const personStatisticStandard = this.personStatisticOrder();
        if (!personStatisticStandard) {
            return;
        }

        await this.updateStatisticsOrder(
            personId,
            personStatisticStandard.statisticOrder ?? [],
            personStatisticStandard.hiddenStatistics ?? [],
        );
    }

    public isFullLayerSelected(): boolean {
        return !!(
            this.selectedLayersAndEntities()!.layers.length && !this.selectedLayersAndEntities()?.entities.length
        );
    }

    public isHalfStatistic(statistic?: ComputedStatistic): boolean {
        return (
            !!statistic &&
            [
                ComputedStatistic.RenderEnum.GAUGE,
                ComputedStatistic.RenderEnum.HALF_GAUGE,
                ComputedStatistic.RenderEnum.PIE,
                ComputedStatistic.RenderEnum.VALUE,
            ].includes(statistic.render)
        );
    }

    public openNewStatisticModal(payloadStatistic: StatisticToValidateDto): void {
        const dialogRef = this.matDialog.open(CreateEditStatisticModalComponent, {
            autoFocus: true,
            data: { payloadStatistic, title: 'createNewStatistic' },
            width: '60rem',
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (!result || !this.isStatisticActive()) {
                return;
            }

            await this.setStatistics();
        });
    }

    public resetGeographicalFilter(): void {
        this.selectedLayersAndEntities.set(undefined);
        this.wktStringMultipolygon.set(undefined);
    }

    public async setStatisticOrder(personId: string): Promise<void> {
        const personStatisticStandard: PersonStatisticStandard = await firstValueFrom(
            this.sdkStatisticService.statisticControllerGetStatisticOrder({
                personId: personId,
                standardId: this.standardId(),
            }),
        );

        this.personStatisticOrder.set(personStatisticStandard);
    }

    public async setStatistics(userId?: string): Promise<void> {
        this.isLoading.set(true);

        try {
            const computedStatisticsResponse = await this.fetchComputedStatistics(this.projectId(), this.standardId());
            this.pendingCharts.set(
                computedStatisticsResponse.computedStatistics.map((item: ComputedStatistic) => item.id),
            );
            this.computedStatisticList.set(computedStatisticsResponse.computedStatistics);

            this.formatStatisticsInformation(computedStatisticsResponse.dataVersion);

            if (!userId) {
                return;
            }
            await this.setStatisticOrder(userId);
        } catch {
            this.snackBarService.openErrorSnackBar('statisticsFetchError');
        } finally {
            this.isLoading.set(false);
        }
    }

    public updateStatisticsOrder(
        personId: string,
        statisticOrder?: string[],
        hiddenStatistics?: string[],
    ): Promise<void> {
        return firstValueFrom(
            this.sdkStatisticService.statisticControllerSaveStatisticOrder({
                upsertPersonStatisticStandardDto: {
                    hiddenStatistics: hiddenStatistics ?? [],
                    person: personId,
                    standard: this.standardId(),
                    statisticOrder: statisticOrder ?? [],
                },
            }),
        );
    }

    private fetchComputedStatistics(projectId: string, standardId: string): Promise<ComputedStatisticsResponse> {
        return firstValueFrom(
            this.projectsService.projectControllerStatistics({
                id: projectId,
                selectedEntities: this.selectedLayersAndEntities()?.entities,
                selectedLayers: this.selectedLayersAndEntities()?.layers,
                standardId: standardId,
                zonesDelimiter: this.wktStringMultipolygon() ? [this.wktStringMultipolygon()!] : [],
            }),
        );
    }

    private formatStatisticsInformation(data: DepositDataVersion[]): void {
        this.dataVersions.set(data);
        this.versionAndDataName.set(
            data.map((depositDataVersion: DepositDataVersion) => ({
                label: `${depositDataVersion.dataName} - ${depositDataVersion.version}`,
                value: `${depositDataVersion.dataName} - ${depositDataVersion.version}`,
            })),
        );
    }
}
